import { useDispatch } from "react-redux";
import { useState, useEffect, useRef, useCallback } from "react";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { AppDispatch } from "app/store";
import { parentsFirebase } from "app/Auth";
import { actions as configActions } from "app/configSlice";
import { updateLastSignInTime } from "features/user/api";
import { createQrLoginSession, pollQrLogin } from "./api";

const POLL_INTERVAL_MS = 2500;

// 新端末（未ログイン）用: QRログインセッションを作成・表示し、承認をポーリングする。
// 承認されたらカスタムトークンでログインする。onSuccess は成功後の遷移等（任意）。
export const useQrLoginDisplay = ({
  enabled,
  onSuccess,
}: {
  enabled: boolean;
  onSuccess?: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [code, setCode] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState(false);
  const secretRef = useRef("");
  const expiresAtRef = useRef<number | null>(null);
  const processingRef = useRef(false);
  // onSuccess は呼び出し側でインライン定義されうるため ref 化し、poll effect を安定させる
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const createSession = useCallback(async () => {
    try {
      setError(false);
      const response = await createQrLoginSession();
      secretRef.current = response.data.session_secret;
      expiresAtRef.current = Date.now() + response.data.expires_in * 1000;
      setRemaining(response.data.expires_in);
      setCode(response.data.code);
    } catch (err) {
      console.error(err);
      setError(true);
    }
  }, []);

  const handleApproved = useCallback(
    async (idToken: string) => {
      if (processingRef.current) return;
      processingRef.current = true;
      try {
        // 残存しているゲストセッションがあると誤ったトークンを返すため、
        // 保護者セッションへの切り替え前に必ずサインアウトする
        const childrenAuth = getAuth();
        if (childrenAuth.currentUser) {
          await childrenAuth.signOut();
        }
        await signInWithCustomToken(getAuth(parentsFirebase), idToken);
        updateLastSignInTime();
        dispatch(configActions.setJustAuthenticated(true));
        onSuccessRef.current?.();
      } catch (err) {
        console.error(err);
        processingRef.current = false;
      }
    },
    [dispatch],
  );

  // セッション作成（有効時）
  useEffect(() => {
    if (enabled) {
      createSession();
    }
  }, [enabled, createSession]);

  // 残り時間のカウントダウン表示
  useEffect(() => {
    if (!enabled || !code) return;
    const timer = setInterval(() => {
      if (expiresAtRef.current) {
        setRemaining(Math.max(0, Math.ceil((expiresAtRef.current - Date.now()) / 1000)));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [enabled, code]);

  // 承認ポーリング（セッションごとに張り直す）
  useEffect(() => {
    if (!enabled || !code || !secretRef.current) return;
    const poll = async () => {
      if (processingRef.current) return;
      // 期限切れ後はサーバーを呼ばずに停止する（再開はユーザーのリフレッシュ操作のみ）
      if (expiresAtRef.current && Date.now() >= expiresAtRef.current) {
        clearInterval(id);
        return;
      }
      try {
        const response = await pollQrLogin({ session_secret: secretRef.current });
        if (response.data.status === "approved" && response.data.idToken) {
          clearInterval(id);
          handleApproved(response.data.idToken);
        } else if (response.data.status === "expired") {
          clearInterval(id);
        }
      } catch (err) {
        console.error(err);
      }
    };
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, code, handleApproved]);

  const expired = remaining <= 0 && code !== "";
  const time = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;

  return { code, remaining, time, expired, error, retry: createSession };
};
